import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Linkedin, Facebook } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const companyInfo = {
  name: "D&J Engineering",
  logo: "https://customer-assets.emergentagent.com/job_bb6c1548-0053-4d1d-9174-d34b5653b3e9/artifacts/43j18h5f_image.png",
  phone: "(819) 775-2130",
  phone2: "(647) 701-4224",
  email: "info@djengineeringroup.ca",
  address: "Gatineau / Ottawa",
};

const oiqLogo = "https://customer-assets.emergentagent.com/job_ingenieriedj/artifacts/iuwsedj7_image.png";

const Footer = () => {
  const { t } = useLanguage();

  const services = [
    t('services.items.structural.title'),
    t('services.items.estimation.title'),
    t('services.items.management.title'),
    t('services.items.bim.title'),
    t('services.items.allinone.title'),
  ];

  const navigation = [
    { name: t('nav.home'), href: "/" },
    { name: t('nav.about'), href: "/about" },
    { name: t('nav.team'), href: "/team" },
    { name: t('nav.contact'), href: "/contact" },
    { name: t('nav.information'), href: "/information" },
    { name: t('nav.blog'), href: "/blog" }
  ];

  return (
    <footer className="bg-slate-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-2 space-y-6">
            <img 
              src={companyInfo.logo} 
              alt={companyInfo.name} 
              className="h-16 w-auto brightness-0 invert"
            />
            <p className="text-slate-400 text-sm leading-relaxed">
              {t('footer.description')}
            </p>
            
            {/* OIQ Certification */}
            <div className="pt-4">
              <p className="text-xs text-slate-500 mb-3 uppercase tracking-wider">{t('footer.certification')}</p>
              <div className="bg-white rounded-lg p-3 inline-block">
                <img 
                  src={oiqLogo} 
                  alt="Ordre des ingénieurs du Québec" 
                  className="h-12 w-auto"
                />
              </div>
            </div>
            
            <div className="flex gap-4 pt-2">
              <a href="#" className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-blue-700 transition-colors">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center hover:bg-blue-700 transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-6">{t('footer.services')}</h3>
            <ul className="space-y-3">
              {services.map((service, idx) => (
                <li key={idx}>
                  <Link 
                    to="/services" 
                    className="text-slate-400 hover:text-blue-400 transition-colors text-sm"
                  >
                    {service}
                  </Link>
                </li>
              ))}
            </ul>
            {/* D&J Engineering Logo Badge */}
            <div className="mt-6 pt-4">
              <div className="bg-white rounded-lg p-3 inline-block">
                <img 
                  src="https://customer-assets.emergentagent.com/job_dj-eng-deploy/artifacts/cti1pnrh_image.png" 
                  alt="D&J Engineering - Residential Structural Design" 
                  className="h-20 w-auto"
                />
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6">{t('footer.navigation')}</h3>
            <ul className="space-y-3">
              {navigation.map((item) => (
                <li key={item.href}>
                  <Link 
                    to={item.href} 
                    className="text-slate-400 hover:text-blue-400 transition-colors text-sm"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-6">{t('footer.contact')}</h3>
            <ul className="space-y-4">
              <li>
                <a href={`tel:${companyInfo.phone}`} className="flex items-start gap-3 text-slate-400 hover:text-blue-400 transition-colors">
                  <Phone className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{companyInfo.phone}</span>
                </a>
              </li>
              <li>
                <a href={`tel:${companyInfo.phone2}`} className="flex items-start gap-3 text-slate-400 hover:text-blue-400 transition-colors">
                  <Phone className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{companyInfo.phone2}</span>
                </a>
              </li>
              <li>
                <a href={`mailto:${companyInfo.email}`} className="flex items-start gap-3 text-slate-400 hover:text-blue-400 transition-colors">
                  <Mail className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{companyInfo.email}</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-slate-400">
                  <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{companyInfo.address}</span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              <img 
                src={companyInfo.logo} 
                alt={companyInfo.name} 
                className="h-10 w-auto brightness-0 invert opacity-70"
              />
              <p className="text-slate-500 text-sm">
                © {new Date().getFullYear()} {companyInfo.name}. {t('footer.rights')}
              </p>
            </div>
            <p className="text-slate-500 text-sm">
              {t('footer.oiqMember')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
